import type { RequestPayload } from "@inertiajs/core"
import { Head, router } from "@inertiajs/react"
import { useState } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

import ReportForm from "./_form"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface Option {
  id: number
  name: string
}

interface ReportData {
  id: number
  report_type: number
  category: number
  report_date: string
  notes: string
  admin_notes: string
  significant: number
  vendor_id: number | null
  investigator_name: string
}

interface Props {
  case_record: CaseRecord
  report: ReportData
  users: Option[]
  vendors: Option[]
  team_members: Option[]
  current_member_ids: number[]
  errors?: Record<string, string[]>
}

export default function Edit({
  case_record,
  report,
  users,
  vendors,
  team_members,
  current_member_ids,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Reports", href: `/cases/${case_record.id}/reports` },
    {
      title: "Edit",
      href: `/cases/${case_record.id}/reports/${report.id}/edit`,
    },
  ]

  const [data, setDataState] = useState({
    report_type: report.report_type,
    category: report.category,
    report_date: report.report_date,
    notes: report.notes,
    admin_notes: report.admin_notes,
    significant: report.significant,
    vendor_id: report.vendor_id,
    investigator_name: report.investigator_name,
  })

  const [teamMemberIds, setTeamMemberIds] = useState<number[]>(
    current_member_ids || [],
  )
  const [processing, setProcessing] = useState(false)

  const setData = (key: keyof typeof data, value: string | number | null) => {
    setDataState((prev) => ({ ...prev, [key]: value }))
  }

  const handleTeamMemberToggle = (id: number) => {
    setTeamMemberIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    )
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.put(
      `/cases/${case_record.id}/reports/${report.id}`,
      {
        case_report: data,
        team_member_ids: teamMemberIds,
      } as unknown as RequestPayload,
      {
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Edit Report - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-4 text-2xl font-semibold">Edit Report</h1>
        <ReportForm
          caseId={case_record.id}
          data={data}
          setData={setData}
          errors={errors}
          processing={processing}
          onSubmit={handleSubmit}
          submitLabel="Update Report"
          users={users}
          vendors={vendors}
          teamMembers={team_members}
          teamMemberIds={teamMemberIds}
          onTeamMemberToggle={handleTeamMemberToggle}
        />
      </div>
    </AppLayout>
  )
}
